import React, { memo, useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import {
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import MembersList from "../Board/components/MembersList/MembersList";
import TextField from "@mui/material/TextField";
import "./styles.scss";
import { useUserStore } from "../../store/user";
import { getUserSlaStatistic } from "../../request/user";
import SlaTasks from "./SlaTasks";
import ExpiredSlaTasks from "./ExpiredSlaTasks";
import WorkIntervals from "./WorkIntervals";
import Export from "../WorkingIntervals/Export";

export default memo(function UserPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { members } = useUserStore();

  const [user, setUser] = useState(null);
  const userId = parseInt(searchParams.get("id"));
  const urlDateFrom = searchParams.get("dateFrom");
  const urlDateTo = searchParams.get("dateTo");

  const [sla, setSla] = useState(null);

  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [showWorkIntervals, setShowWorkIntervals] = useState(false);
  const [showRateTasks, setShowRateTasks] = useState(false);
  const [showExpiredCommentTasks, setShowExpiredCommentTasks] = useState(false);
  const [showExpiredStatusTasks, setShowExpiredStatusTasks] = useState(false);
  const [showExpiredLifetimeTasks, setShowExpiredLifetimeTasks] =
    useState(false);

  const dateFormat = "YYYY-MM-DD";

  const [dateFrom, setDateFrom] = useState(
    urlDateFrom || moment().format(dateFormat),
  );
  const [dateTo, setDateTo] = useState(
    urlDateTo || moment().format(dateFormat),
  );

  useEffect(() => {
    if (members && userId) {
      let user = members.find((member) => member.id === userId);
      setUser(user);
    }
  }, [members, userId]);

  useEffect(() => {
    const get = async () => {
      await getData({});
    };

    get().catch((err) => console.error(err));
  }, [searchParams, dateFrom, dateTo]);

  const getData = useCallback(
    async (data) => {
      if (!parseInt(searchParams.get("id"))) return;
      try {
        let params = {
          assignee_id: [parseInt(searchParams.get("id"))],
          date_from: dateFrom ?? moment().format("YYYY-MM-DD"),
          date_to: dateTo,
        };

        const response = await getUserSlaStatistic(params).catch((err) => {});
        setSla(response?.data);
      } catch (e) {
        console.log(e);
      }
    },
    [searchParams, dateFrom, dateTo],
  );

  const formatHoursDuration = (time) => {
    let hours = Math.floor(time / (60 * 60));
    let minutes = Math.floor((time / 60) % 60);

    return (
      (hours < 10 ? "0" + hours : hours) +
      "h " +
      (minutes > 0 ? (minutes < 10 ? "0" + minutes : minutes) + "min " : "")
    );
  };

  const onChangeAssigneeHandler = useCallback((params) => {
    setSearchParams({ id: params.value });
    navigate(`/user?id=${encodeURIComponent(params.value)}`);
  }, []);

  const handleDateChange = useCallback((code, value) => {
    switch (code) {
      case "date_from":
        setDateFrom(value);
        break;
      case "date_to":
        setDateTo(value);
        break;
      default:
        break;
    }
  }, []);

  return (
    <>
      <Grid container spacing="15px">
        <Grid item xs={12} md={12}>
          <div className="flex-start mb-15">
            <div>
              {user && (
                <Typography variant="h6">
                  {user.name} - {user.email}
                </Typography>
              )}
            </div>
            <div className="pl-15"></div>
          </div>
        </Grid>
        <Grid item xs={12} md={3}>
          {
            <TextField
              type="date"
              label="Date from"
              defaultValue={
                urlDateFrom ? urlDateFrom : moment().format(dateFormat)
              }
              size="small"
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(e) => handleDateChange("date_from", e.target.value)}
              autoComplete="off"
            />
          }
        </Grid>
        <Grid item xs={12} md={3}>
          {
            <TextField
              type="date"
              label="Date to (including)"
              defaultValue={urlDateTo ? urlDateTo : moment().format(dateFormat)}
              size="small"
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              onChange={(e) => handleDateChange("date_to", e.target.value)}
              autoComplete="off"
            />
          }
        </Grid>
        <Grid item xs={12} md={6}>
          {
            <MembersList
              multiple={false}
              defaultAssignee={userId}
              onChange={onChangeAssigneeHandler}
            />
          }
        </Grid>
      </Grid>
      {sla && (
        <div style={{ margin: "20px 0" }}>
          <List>
            <ListItem>
              <ListItemText>
                <div className="flex">
                  <div>{`Completed tasks: ${sla.completed?.count}`}</div>

                  <Button
                    variant="outlined"
                    onClick={() => setShowCompletedTasks((prev) => !prev)}
                  >
                    {showCompletedTasks ? "Hide tasks" : "Show tasks"}
                  </Button>
                </div>
                {showCompletedTasks && (
                  <div style={{ marginTop: "20px" }}>
                    <SlaTasks tasks={sla.completed?.tasks} />
                  </div>
                )}
              </ListItemText>
            </ListItem>
            <hr />
            <ListItem>
              <ListItemText>
                <div className="flex">
                  <div>{`Work time: ${formatHoursDuration(sla.in_progress_time)}`}</div>

                  <Button
                    variant="outlined"
                    onClick={() => setShowWorkIntervals((prev) => !prev)}
                  >
                    {showWorkIntervals ? "Hide tasks" : "Show tasks"}
                  </Button>
                </div>
                {showWorkIntervals && (
                  <div style={{ marginTop: "20px" }}>
                    <WorkIntervals items={sla.in_progress_time_intervals} />
                  </div>
                )}
              </ListItemText>
            </ListItem>
            <hr />
            <ListItem>
              <ListItemText>
                <div className="flex">
                  <div>{`Rate average: ${Number(sla.rate?.avg ?? 0).toFixed(1)}`}</div>

                  <Button
                    variant="outlined"
                    onClick={() => setShowRateTasks((prev) => !prev)}
                  >
                    {showRateTasks ? "Hide tasks" : "Show tasks"}
                  </Button>
                </div>
                {showRateTasks && (
                  <div style={{ marginTop: "20px" }}>
                    <SlaTasks tasks={sla.rate.tasks} />
                  </div>
                )}
              </ListItemText>
            </ListItem>
            <hr />
            <ListItem>
              <ListItemText>
                <div className="flex">
                  <div>{`Expired first comment sla: ${sla.expired?.first_comment?.count}`}</div>

                  <Button
                    variant="outlined"
                    onClick={() => setShowExpiredCommentTasks((prev) => !prev)}
                  >
                    {showExpiredCommentTasks ? "Hide tasks" : "Show tasks"}
                  </Button>
                </div>
                {showExpiredCommentTasks && (
                  <div style={{ marginTop: "20px" }}>
                    <ExpiredSlaTasks
                      tasks={sla.expired?.first_comment?.tasks}
                    />
                  </div>
                )}
              </ListItemText>
            </ListItem>
            <hr />
            <ListItem>
              <ListItemText>
                <div className="flex">
                  <div>{`Expired in progress sla: ${sla.expired?.in_progress?.count}`}</div>

                  <Button
                    variant="outlined"
                    onClick={() => setShowExpiredStatusTasks((prev) => !prev)}
                  >
                    {showExpiredStatusTasks ? "Hide tasks" : "Show tasks"}
                  </Button>
                </div>
                {showExpiredStatusTasks && (
                  <div style={{ marginTop: "20px" }}>
                    <ExpiredSlaTasks tasks={sla.expired?.in_progress?.tasks} />
                  </div>
                )}
              </ListItemText>
            </ListItem>
            <hr />
            <ListItem>
              <ListItemText>
                <div className="flex">
                  <div>{`Expired lifetime sla: ${sla.expired?.lifetime?.count}`}</div>

                  <Button
                    variant="outlined"
                    onClick={() => setShowExpiredLifetimeTasks((prev) => !prev)}
                  >
                    {showExpiredLifetimeTasks ? "Hide tasks" : "Show tasks"}
                  </Button>
                </div>
                {showExpiredLifetimeTasks && (
                  <div style={{ marginTop: "20px" }}>
                    <ExpiredSlaTasks tasks={sla.expired?.lifetime?.tasks} />
                  </div>
                )}
              </ListItemText>
            </ListItem>
            <hr />
          </List>

          <div style={{ margin: "40px 0" }}>
            <Export
              dateFromValue={dateFrom}
              dateToValue={dateTo}
              assigneeIdsArray={[parseInt(searchParams.get("id"))]}
              type="user-sla"
            />
          </div>
        </div>
      )}
    </>
  );
});
